import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trackDoseWiseEvents } from '../utils/analytics';
import { PEN_STRENGTHS, CLICKS_PER_DOSE, MEDICAL_LINKS, LANDING_COPY } from '../utils/constants';
import '../App.css';
import AdSlot from '../components/AdSlot';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface Pen {
  _id: string;
  strength: number;
  startDate: string;
  remainingClicks: number;
  remainingDoses: number;
  status: 'active' | 'empty' | 'expired' | 'retired';
  name: string;
}

function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [clicks, setClicks] = useState(0);
  const [penStrength, setPenStrength] = useState(2.5);
  // Raw text shown in the Dose Amount field. Kept as a string (rather than a
  // parsed number) so a partially-typed value (e.g. "7," or "7.") survives until
  // the user finishes, and so both "." and "," decimal separators are accepted.
  // The clicks slider is the numeric source of truth for the calculated dose.
  const [doseInput, setDoseInput] = useState('0');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const fetchDoseHistory = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/doses`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        console.error('Dose history data is not an array:', data);
        return;
      }

      // setDoseHistory(sortedDoses); // This line was removed as per the edit hint
    } catch (error) {
      console.error('Error fetching dose history:', error);
    }
  }, [isAuthenticated]);

  const fetchActivePens = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // setPens([]); // This line was removed as per the edit hint
        // setSelectedPenIndex(null); // This line was removed as per the edit hint
        return;
      }

      const response = await fetch(`${API_BASE_URL}/pens`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch pens: ${response.status}`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        // setPens([]); // This line was removed as per the edit hint
        // setSelectedPenIndex(null); // This line was removed as per the edit hint
        return;
      }

      const sortedPens = data
        .filter((pen: Pen) => pen && typeof pen === 'object')
        .sort((a: Pen, b: Pen) => {
          const aStatus = a.status || 'empty';
          const bStatus = b.status || 'empty';
          if (aStatus === 'active' && bStatus !== 'active') return -1;
          if (aStatus !== 'active' && bStatus === 'active') return 1;
          const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
          const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
          return bDate - aDate;
        });

      // setPens(sortedPens); // This line was removed as per the edit hint

      const activePenIndex = sortedPens.findIndex((pen: Pen) => pen.status === 'active');
      if (activePenIndex !== -1) {
        // setSelectedPenIndex(activePenIndex); // This line was removed as per the edit hint
        setPenStrength(sortedPens[activePenIndex].strength);
      } else {
        // setSelectedPenIndex(null); // This line was removed as per the edit hint
      }
    } catch (error) {
      console.error('Error fetching pens:', error);
      // setPens([]); // This line was removed as per the edit hint
      // setSelectedPenIndex(null); // This line was removed as per the edit hint
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Landing page view tracking
    try { trackDoseWiseEvents.landingPageViewed(document.referrer || undefined); } catch {}
    if (isAuthenticated) {
      fetchDoseHistory();
      fetchActivePens();
    }
  }, [isAuthenticated, fetchDoseHistory, fetchActivePens]);

  const handleDoseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDoseInput(raw);
    // Accept both "." and "," as the decimal separator so European users can
    // enter e.g. "7,5". Fall back to 0 for empty/invalid input.
    const parsed = Number(raw.replace(/,/g, '.'));
    const newDose = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    const newClicks = Math.round((newDose / penStrength) * CLICKS_PER_DOSE);
    setClicks(Math.min(60, Math.max(0, newClicks)));
    trackDoseWiseEvents.doseLogged(newDose, penStrength, false);
    if (!isAuthenticated) {
      try { trackDoseWiseEvents.calculatorInteracted(penStrength); } catch {}
    }
  };

  const calculateDose = () => {
    return (clicks / CLICKS_PER_DOSE) * penStrength;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-stone-50 to-orange-50 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-lg rounded-2xl border border-stone-200 p-8 mb-8">
          <h1 className="text-4xl font-black mb-6 font-['Space_Grotesk'] bg-gradient-to-r from-emerald-600 via-teal-600 to-orange-500 bg-clip-text text-transparent text-center">
            Mounjaro Dose Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2 uppercase tracking-wider">Pen Strength</label>
              <select
                className="form-select w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-emerald-500 focus:ring-emerald-100"
                value={penStrength}
                onChange={e => setPenStrength(Number(e.target.value))}
              >
                {PEN_STRENGTHS.map(strength => (
                  <option key={strength} value={strength}>{strength} mg</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2 uppercase tracking-wider">Dose Amount</label>
              <div className="flex items-center">
                <input
                  className="form-input w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-emerald-500 focus:ring-emerald-100"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.,]?[0-9]*"
                  value={doseInput}
                  onChange={handleDoseChange}
                  style={{ appearance: 'textfield' }}
                />
                <span className="ml-2 text-stone-500">mg</span>
              </div>
            </div>
          </div>
          <div className="slider-container">
            <label className="form-label">Clicks</label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max={CLICKS_PER_DOSE}
                value={clicks}
                onChange={e => setClicks(Number(e.target.value))}
                className="clicks-slider"
              />
              <span className="slider-value">{clicks}</span>
            </div>
          </div>
          <div className="mt-8">
            <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-orange-400 text-white p-8 rounded-2xl text-center my-8 shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-2 uppercase tracking-wider">Calculated Dose</div>
              <div className="text-5xl font-black font-['Space_Grotesk']">{calculateDose().toFixed(2)}mg</div>
            </div>
            {!isAuthenticated && (
              <div className="flex flex-col items-center justify-center gap-4">
                <button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-md w-full sm:w-auto"
                  onClick={() => { try { trackDoseWiseEvents.ctaClicked('hero_register', 'hero'); } catch {} navigate('/register'); }}
                >
                  {LANDING_COPY.heroCtaPrimary}
                </button>
                <div className="text-center">
                  <span className="text-stone-600 text-sm">Already have an account? </span>
                  <button
                    className="text-emerald-600 hover:text-emerald-700 font-semibold text-sm underline transition-colors"
                    onClick={() => { 
                      try { trackDoseWiseEvents.ctaClicked('hero_login', 'hero'); } catch {} 
                      navigate('/login'); 
                    }}
                  >
                    Welcome back! Sign in to continue your journey →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Ad Slot 1: below hero */}
        {process.env.REACT_APP_ENABLE_ADS === 'true' && !isAuthenticated && (
          <AdSlot
            slotId={process.env.REACT_APP_ADSENSE_SLOT_ID || ''}
            testMode={process.env.NODE_ENV !== 'production'}
            className="my-6 bg-white rounded-2xl border border-stone-200 overflow-hidden"
          />
        )}

        {/* Why Register */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mt-8">
          <h2 className="text-2xl font-bold text-stone-900 font-['Space_Grotesk'] mb-2">Why create a free account?</h2>
          <p className="text-stone-600 mb-4">Track your doses, weight and side effects across devices — and remove ads.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {LANDING_COPY.benefits.map((b, i) => (
              <div key={i} className="rounded-xl border border-stone-200 p-4 bg-stone-50">
                <div className="text-lg font-semibold text-stone-900">{b.title}</div>
                <div className="text-stone-700 text-sm mt-1">{b.text}</div>
              </div>
            ))}
          </div>
          {!isAuthenticated && (
            <div className="mt-4">
              <button
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-semibold"
                onClick={() => { try { trackDoseWiseEvents.ctaClicked('why_register', 'benefits'); } catch {} navigate('/register'); }}
              >
                Start Tracking Free →
              </button>
            </div>
          )}
        </div>

        {/* How It Works */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mt-8">
          <h2 className="text-2xl font-bold text-stone-900 font-['Space_Grotesk'] mb-4">How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {LANDING_COPY.howItWorks.map((step, i) => (
              <div key={i} className="rounded-xl border border-stone-200 p-4 bg-stone-50">
                <div className="text-lg font-semibold text-stone-900">{i + 1}. {step.title}</div>
                <div className="text-stone-700 text-sm mt-1">{step.text}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Educational Content: SEO-rich accordions */}
        <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6 mt-8">
          <h2 className="text-2xl font-bold text-stone-900 font-['Space_Grotesk'] mb-4">About Mounjaro Dosing</h2>
          <div className="divide-y divide-stone-200">
            {[
              { id: 'clicks', title: 'Understanding the KwikPen click system', content: (
                <ul className="list-disc list-inside text-stone-700">
                  <li>Each full dose equals 60 clicks.</li>
                  <li>Each pen contains 4 full doses (240 clicks total).</li>
                  <li>Fractional dosing helps reduce waste and tailor your dose.</li>
                  <li>
                    See the official guide: <a className="text-emerald-700 underline" href={MEDICAL_LINKS.eliLillyMounjaro} target="_blank" rel="noreferrer" onClick={() => trackDoseWiseEvents.externalLinkClicked('eli_lilly_mounjaro')}>Eli Lilly Mounjaro</a>
                  </li>
                </ul>
              )},
              { id: 'escalation', title: 'Dose escalation schedule', content: (
                <ul className="list-disc list-inside text-stone-700">
                  <li>Start at 2.5mg and increase in 2.5mg steps.</li>
                  <li>Wait at least 4 weeks between increases.</li>
                  <li>Maintenance doses typically range from 2.5mg to 15mg.</li>
                  <li>
                    Prescribing information: <a className="text-emerald-700 underline" href={MEDICAL_LINKS.fdaPrescribingInfo} target="_blank" rel="noreferrer" onClick={() => trackDoseWiseEvents.externalLinkClicked('fda_pi')}>FDA label</a>
                  </li>
                </ul>
              )},
              { id: 'costs', title: 'Cost optimization strategies', content: (
                <ul className="list-disc list-inside text-stone-700">
                  <li>Track real cost-per-dose to identify savings.</li>
                  <li>Avoid waste by timing dose increases with pen usage.</li>
                  <li>Extract remaining medication safely when appropriate.</li>
                  <li>
                    Local info: <a className="text-emerald-700 underline" href={MEDICAL_LINKS.tgaProductInfo} target="_blank" rel="noreferrer" onClick={() => trackDoseWiseEvents.externalLinkClicked('tga_pi')}>TGA product info</a>
                  </li>
                </ul>
              )},
              { id: 'side_effects', title: 'Managing side effects', content: (
                <ul className="list-disc list-inside text-stone-700">
                  <li>Common GI symptoms often improve over time.</li>
                  <li>Logging symptoms helps you and your clinician spot patterns.</li>
                  <li>Seek medical advice if symptoms are severe or persistent.</li>
                  <li>
                    Learn more: <a className="text-emerald-700 underline" href={MEDICAL_LINKS.nhsGlp1Info} target="_blank" rel="noreferrer" onClick={() => trackDoseWiseEvents.externalLinkClicked('nhs_glp1')}>NHS GLP‑1 guidance</a>
                  </li>
                </ul>
              )},
            ].map((sec) => (
              <div key={sec.id}>
                <button
                  className="w-full text-left py-3 font-semibold text-stone-900 flex justify-between items-center"
                  onClick={() => {
                    const next = expandedSection === sec.id ? null : sec.id;
                    setExpandedSection(next);
                    if (next) trackDoseWiseEvents.educationalSectionExpanded(sec.id);
                  }}
                  aria-expanded={expandedSection === sec.id}
                >
                  <span>{sec.title}</span>
                  <span className="text-stone-500">{expandedSection === sec.id ? '−' : '+'}</span>
                </button>
                {expandedSection === sec.id && (
                  <div className="pb-4">{sec.content}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Medical Disclaimer */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mt-8">
          <h2 className="text-xl font-bold text-stone-900 mb-2">{LANDING_COPY.disclaimerTitle}</h2>
          <p className="text-stone-700 text-sm">{LANDING_COPY.disclaimerBody}</p>
        </div>
        {/* Ad Slot: Bottom of page, above final CTA */}
        {process.env.REACT_APP_ENABLE_ADS === 'true' && !isAuthenticated && (
          <AdSlot
            slotId={process.env.REACT_APP_ADSENSE_SLOT_ID || ''}
            testMode={process.env.NODE_ENV !== 'production'}
            className="my-8 bg-white rounded-2xl border border-stone-200 overflow-hidden"
          />
        )}

        {!isAuthenticated && (
          <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-orange-500 text-white p-6 rounded-2xl mt-6 text-center">
            <div className="text-2xl font-bold mb-1">{LANDING_COPY.finalCtaTitle}</div>
            <div className="opacity-90 mb-4">{LANDING_COPY.finalCtaSub}</div>
            <button
              className="bg-white text-emerald-700 hover:bg-stone-50 px-6 py-3 rounded-lg font-semibold mb-3"
              onClick={() => { try { trackDoseWiseEvents.ctaClicked('final_register', 'final_cta'); } catch {} navigate('/register'); }}
            >
              Create Free Account
            </button>
            <div className="text-white/80 text-sm">
              <span>Returning member? </span>
              <button
                className="text-white hover:text-white/90 font-semibold underline transition-colors"
                onClick={() => { 
                  try { trackDoseWiseEvents.ctaClicked('final_login', 'final_cta'); } catch {} 
                  navigate('/login'); 
                }}
              >
                Sign in to access your dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HomePage; 